import api from './api';
import { apiCache, cacheTTL } from '@/utils/api-cache';

/**
 * CAIR (California Immunization Registry) service
 * Vaccine administration recording + CAIR submission tracking
 */

// Event dispatched whenever administrations change (recorded / resubmitted)
// so independent sections can refetch without prop drilling.
export const ADMINISTRATIONS_UPDATED_EVENT = 'cair:administrations-updated';

export function notifyAdministrationsUpdated(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ADMINISTRATIONS_UPDATED_EVENT));
  }
}

export type CairStatus =
  | 'queued'
  | 'submitted'
  | 'accepted'
  | 'accepted_with_errors'
  | 'rejected'
  | 'blocked_missing_data'
  | 'failed';

export interface VaccineAdministration {
  id: string;
  recommendation_selection_id: string | null;
  protocol_key: string;
  cvx_code: string;
  mvx_code: string | null;
  lot_number: string;
  expiration_date: string;
  dose_amount: number | string | null;
  dose_units: string | null;
  route_code: string | null;
  body_site_code: string | null;
  vis_publication_date: string | null;
  vis_presented_date: string | null;
  administered_at: string;
  information_source: string | null;
  dose_number: number | null;
  notes: string | null;
  cair_status: CairStatus;
  cair_ack_code: string | null;
  cair_ack_details: string | null;
  cair_error_message: string | null;
  cair_submission_id: string | null;
  cair_submitted_at: string | null;
}

export interface ListAdministrationsResponse {
  administrations: VaccineAdministration[];
}

export interface RecordAdministrationRequest {
  recommendation_selection_id?: string;
  protocol_key: string;
  cvx_code: string;
  mvx_code?: string;
  lot_number: string;
  expiration_date: string; // YYYY-MM-DD
  dose_amount?: number;
  dose_units?: string;
  route_code?: string;
  body_site_code?: string;
  vis_publication_date?: string; // YYYY-MM-DD
  vis_presented_date?: string; // YYYY-MM-DD
  administered_at: string; // ISO datetime
  information_source?: '00' | '01';
  dose_number?: number;
  notes?: string;
}

export interface RecordAdministrationResponse {
  administration_id: string;
  cair_submission_id: string | null;
  cair_status: 'queued' | 'blocked_missing_data';
  missing_demographics: string[];
}

export interface ResubmitCairResponse {
  cair_submission_id?: string | null;
  cair_status: string;
  missing_demographics?: string[];
}

export interface UpdateDemographicsRequest {
  sex?: 'M' | 'F' | 'X' | 'U';
  middle_name?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  race_code?: string;
  ethnicity_code?: string;
  mothers_maiden_name?: string;
  multiple_birth_indicator?: string;
}

export interface CvxMapping {
  protocol_key: string;
  cvx_code: string;
  cvx_description: string;
  default_mvx_code: string | null;
  is_default: boolean;
}

export interface CairReferenceCodes {
  cvx_mappings: CvxMapping[];
  manufacturers: Record<string, string>;
  routes: Record<string, string>;
  body_sites: Record<string, string>;
  sites_by_route: Record<string, string[]>;
  race_codes: Record<string, string>;
  ethnicity_codes: Record<string, string>;
  sex_codes: Record<string, string>;
}

/**
 * Get CAIR reference codes (CVX mappings, manufacturers, routes, body sites,
 * race/ethnicity/sex codes). Cached — this data rarely changes.
 */
export async function getReferenceCodes(): Promise<CairReferenceCodes> {
  return apiCache.fetch(
    'cair:reference-codes',
    async () => {
      const response = await api.get<CairReferenceCodes>('/cair/reference/codes');
      return response.data;
    },
    cacheTTL.long // 15 minutes - rarely changing reference data
  );
}

/**
 * List recorded vaccine administrations for a traveler
 */
export async function listAdministrations(
  travelerId: string
): Promise<VaccineAdministration[]> {
  const response = await api.get<ListAdministrationsResponse>(
    `/pharmacy/travelers/${travelerId}/administrations`
  );
  return response.data.administrations;
}

/**
 * Record a vaccine administration for a traveler (queues a CAIR submission)
 */
export async function recordAdministration(
  travelerId: string,
  payload: RecordAdministrationRequest
): Promise<RecordAdministrationResponse> {
  const response = await api.post<RecordAdministrationResponse>(
    `/pharmacy/travelers/${travelerId}/administrations`,
    payload
  );
  return response.data;
}

/**
 * Re-submit an administration to CAIR (after fixing demographics or a failure)
 */
export async function resubmitCairSubmission(
  administrationId: string
): Promise<ResubmitCairResponse> {
  const response = await api.post<ResubmitCairResponse>(
    `/pharmacy/administrations/${administrationId}/resubmit`
  );
  return response.data;
}

/**
 * Update traveler demographics required for CAIR submissions
 */
export async function updateTravelerDemographics(
  travelerId: string,
  payload: UpdateDemographicsRequest
): Promise<void> {
  await api.patch(`/pharmacy/travelers/${travelerId}/demographics`, payload);
}

export const cairService = {
  getReferenceCodes,
  listAdministrations,
  recordAdministration,
  resubmitCairSubmission,
  updateTravelerDemographics,
};
